/**
 * Module-level reference to the active `ConversationStore`.
 *
 * Zustand slices in this folder are global by design — there is exactly
 * one End_User and exactly one persistence backend at any time, and
 * threading the store through React props would buy nothing. The boot
 * sequence opens the store via `openConversationStore()` once and wires
 * the result here; slices then look it up lazily on every read/write.
 *
 * The reference starts as `null`; calling any slice method before the
 * boot sequence has wired the store throws a clear error (as opposed to
 * a confusing `undefined.method is not a function`). This keeps the
 * temporal coupling between boot and slices explicit.
 *
 * `inMemoryFallback` is captured at the same time so the persistence-
 * unavailable banner (Requirement 12.2) can be surfaced from anywhere
 * in the React tree without re-checking the adapter.
 */

import type { ConversationStore } from '../infra/store';

let storeRef: ConversationStore | null = null;
let inMemoryFlag = false;

/**
 * Wire the active `ConversationStore` for the current boot.
 *
 * Called once by `App.tsx` after `openConversationStore()` resolves.
 * Idempotent: calling it again replaces the reference (useful in tests
 * that need to swap backends mid-suite).
 */
export function setActiveConversationStore(
  store: ConversationStore,
  inMemoryFallback: boolean,
): void {
  storeRef = store;
  inMemoryFlag = inMemoryFallback;
}

/**
 * Return the active `ConversationStore` or throw a clear error if the
 * boot sequence has not run yet. Throwing here is intentional — every
 * slice that needs the store assumes boot has completed, so silently
 * returning `null` would mask the bug.
 */
export function getActiveConversationStore(): ConversationStore {
  if (storeRef === null) {
    throw new Error(
      'Conversation store not initialized. Call setActiveConversationStore() during boot.',
    );
  }
  return storeRef;
}

/**
 * `true` iff the active adapter is the in-memory fallback (Requirement 12.2).
 *
 * Components use this to render the sticky "messages will not persist
 * after this tab closes" banner.
 */
export function isInMemoryFallback(): boolean {
  return inMemoryFlag;
}

/**
 * Test-only reset hook. Clears the module-level reference so a fresh
 * boot sequence can run in a unit test without leaking state from the
 * previous test case. Not exported from the barrel — tests import it
 * directly from this module.
 */
export function __resetActiveConversationStoreForTests(): void {
  storeRef = null;
  inMemoryFlag = false;
}
